/**
 * MailerLite integration contracts (admin-managed).
 *
 * PSA pushes contacts one-way into a MailerLite group. Which contacts qualify is decided
 * by an admin-authored boolean filter tree (nested AND/OR), and every contact is pushed at
 * most once — a per-contact ledger records the push so later runs skip it.
 */
/** Default MailerLite group PSA pushes contacts into. Admin-editable. */
export const MAILERLITE_DEFAULT_GROUP_NAME = 'Catalyst PSA Contacts';
export const MAILERLITE_MIN_INTERVAL_MINUTES = 15;
export const MAILERLITE_MAX_INTERVAL_MINUTES = 10_080;
export const MAILERLITE_DEFAULT_INTERVAL_MINUTES = 1_440;
/** Hard cap on how many contacts a single run will push, so a run can't hang. */
export const MAILERLITE_MAX_RUN_BATCH = 500;
/**
 * MailerLite's documented global limit is 120 requests/minute, and a 429 can carry a
 * Retry-After of ~2 minutes. Pace below the limit so a large run never trips it.
 */
export const MAILERLITE_REQUESTS_PER_MINUTE = 100;
/**
 * Wall-clock budget for one run. Paced requests make a full batch slow, so a run stops at
 * the budget and defers the rest to the next run rather than holding the HTTP call open.
 */
export const MAILERLITE_RUN_BUDGET_MS = 4 * 60_000;
/** Attempts per contact (1 initial + retries) before it is recorded as failed. */
export const MAILERLITE_MAX_ATTEMPTS = 4;
/** Hard cap on subscriber-status lookups per refresh, to stay inside MailerLite's rate limit. */
export const MAILERLITE_MAX_STATUS_BATCH = 300;
// ---------------------------------------------------------------------------
// Subscriber status
// ---------------------------------------------------------------------------
/**
 * Subscriber states MailerLite reports. `junk` is MailerLite's wire value for a spam
 * complaint; the UI shows it as "Spam complaint".
 */
export const MAILERLITE_SUBSCRIBER_STATUSES = [
    'active',
    'unsubscribed',
    'unconfirmed',
    'bounced',
    'junk',
];
export const MAILERLITE_STATUS_LABELS = {
    active: 'Active',
    unsubscribed: 'Unsubscribed',
    unconfirmed: 'Unconfirmed',
    bounced: 'Bounced',
    junk: 'Spam complaint',
};
/** Narrows an arbitrary MailerLite `status` value to a known one, else null. */
export function toMailerliteStatus(raw) {
    const value = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
    // MailerLite has used both "junk" and "spam" for complaints across API versions.
    if (value === 'spam')
        return 'junk';
    return MAILERLITE_SUBSCRIBER_STATUSES.includes(value)
        ? value
        : null;
}
/** Longest push error kept on the contact; matches the column width. */
export const MAILERLITE_ERROR_MAX_CHARS = 512;
/**
 * Trims a MailerLite failure down to the part worth showing on a badge.
 *
 * A validation failure arrives as
 * `The given data was invalid. — email: The email must be a valid email address.`
 * where everything before the `email:` field label is boilerplate, so the label and
 * anything ahead of it is dropped. Messages without that label are kept whole.
 */
export function shortenMailerliteError(raw) {
    const text = raw?.trim();
    if (!text)
        return null;
    const match = /email\s*:\s*/i.exec(text);
    const trimmed = (match ? text.slice(match.index + match[0].length) : text).trim();
    const result = trimmed || text;
    return result.length > MAILERLITE_ERROR_MAX_CHARS
        ? result.slice(0, MAILERLITE_ERROR_MAX_CHARS)
        : result;
}
// ---------------------------------------------------------------------------
// Filter DSL
// ---------------------------------------------------------------------------
/** Field kinds a filter condition can target; drives the allowed operators. */
export const MAILERLITE_FIELD_TYPES = ['string', 'boolean', 'enum', 'uuid', 'date'];
export const MAILERLITE_OPERATORS = [
    'eq',
    'neq',
    'contains',
    'not_contains',
    'starts_with',
    'in',
    'is_true',
    'is_false',
    'is_empty',
    'is_not_empty',
    'before',
    'after',
    'between',
];
/** Operators that carry no value (the operator alone is the whole test). */
export const MAILERLITE_VALUELESS_OPERATORS = [
    'is_true',
    'is_false',
    'is_empty',
    'is_not_empty',
];
/** Which operators make sense for each field type; the UI offers only these. */
export const MAILERLITE_OPERATORS_BY_TYPE = {
    string: ['contains', 'not_contains', 'starts_with', 'eq', 'neq', 'is_empty', 'is_not_empty'],
    boolean: ['is_true', 'is_false'],
    enum: ['eq', 'neq', 'in', 'is_empty', 'is_not_empty'],
    uuid: ['eq', 'neq', 'in', 'is_empty', 'is_not_empty'],
    date: ['before', 'after', 'between', 'is_empty', 'is_not_empty'],
};
export const MAILERLITE_OPERATOR_LABELS = {
    eq: 'is',
    neq: 'is not',
    contains: 'contains',
    not_contains: 'does not contain',
    starts_with: 'starts with',
    in: 'is any of',
    is_true: 'is checked',
    is_false: 'is not checked',
    is_empty: 'is empty',
    is_not_empty: 'is not empty',
    before: 'is before',
    after: 'is after',
    between: 'is between',
};
/** An empty root group — matches every contact. */
export function emptyMailerliteFilter() {
    return { kind: 'group', match: 'all', children: [] };
}
/** The contact fields available to the filter builder. */
export const MAILERLITE_CONTACT_FIELDS = [
    { key: 'firstName', label: 'First name', type: 'string' },
    { key: 'lastName', label: 'Last name', type: 'string' },
    { key: 'email', label: 'Email', type: 'string' },
    { key: 'phone', label: 'Phone', type: 'string' },
    { key: 'title', label: 'Title', type: 'string' },
    { key: 'customerName', label: 'Customer', type: 'string' },
    { key: 'customerId', label: 'Customer ID', type: 'uuid' },
    { key: 'isActive', label: 'Active', type: 'boolean' },
    { key: 'isPrimary', label: 'Primary contact', type: 'boolean' },
    { key: 'sendToMailerlite', label: 'Send to MailerLite', type: 'boolean' },
    {
        key: 'mailerliteStatus',
        label: 'MailerLite subscriber status',
        type: 'enum',
        options: MAILERLITE_SUBSCRIBER_STATUSES.map((value) => ({
            value,
            label: MAILERLITE_STATUS_LABELS[value],
        })),
    },
    { key: 'billingContact', label: 'Billing contact', type: 'boolean' },
    { key: 'projectContact', label: 'Project contact', type: 'boolean' },
    { key: 'supportContact', label: 'Support contact', type: 'boolean' },
    { key: 'ticketManager', label: 'Ticket manager', type: 'boolean' },
    { key: 'catPlusNotifications', label: 'Cat+ notifications', type: 'boolean' },
    { key: 'referenceable', label: 'Referenceable', type: 'boolean' },
    {
        key: 'supportTicketAccess',
        label: 'Support ticket access',
        type: 'enum',
        options: [
            { value: 'no_access', label: 'No access' },
            { value: 'ticket_user', label: 'Ticket user' },
            { value: 'ticket_manager', label: 'Ticket manager' },
        ],
    },
    {
        key: 'aiAccess',
        label: 'AI access',
        type: 'enum',
        options: [
            { value: 'no_access', label: 'No access' },
            { value: 'ai_user', label: 'AI user' },
            { value: 'ai_manager', label: 'AI manager' },
        ],
    },
    {
        key: 'adminAccess',
        label: 'Admin access',
        type: 'enum',
        options: [
            { value: 'no_access', label: 'No access' },
            { value: 'billing_admin', label: 'Billing admin' },
        ],
    },
    {
        key: 'source',
        label: 'Source',
        type: 'enum',
        options: [
            { value: 'native', label: 'Native' },
            { value: 'connectwise', label: 'ConnectWise' },
        ],
    },
    { key: 'lastReferenceRequest', label: 'Last reference request', type: 'date' },
    { key: 'createdAt', label: 'Created', type: 'date' },
    { key: 'updatedAt', label: 'Updated', type: 'date' },
];
/** Cap on run headers returned to the log panel. */
export const MAILERLITE_RUN_HISTORY_LIMIT = 25;
//# sourceMappingURL=mailerlite.js.map